/**
 * Whatever is open over the package builder — the quote's editor layer, fed
 * the package adapter (`builder-engine-and-consistency.md` §7). Every box here
 * is the quote's own popover; what differs is where its Done goes: into the
 * draft, rather than to a quote endpoint.
 */
import {
  BILLING_KIND_CARDS, BILLING_TAB, NO_SERVICE_NOTES, QUOTE_LINE_KIND_LABELS, TERMS_WORDS,
  planWordsContext, type PackageVisitSpec,
} from '@rowted/shared'
import { ContextMenu } from '../components/context-menu.js'
import { VisitMarginPopover } from '../builder/margin/visit-margin.js'
import { RecipeLinePopover } from '../builder/popovers/line-popover.js'
import { ServicePopover } from '../builder/popovers/service-popover.js'
import { VisitPopover } from '../builder/popovers/visit-popover.js'
import { EditorActions, EditorHeader, EditorShell } from '../builder/editor-shell.js'
import { KindPanel } from '../quote/billing/kind-panel.js'
import { TermsSelect } from '../components/terms-select.js'
import { IconMoney } from '../builder/rows/glyphs.js'
import { SheetOrDialog } from '../components/sheet-or-dialog.js'
import { ProposalVisitPopover } from '../quote/proposal-visit-popover.js'
import { AddSearch } from '../quote/quote-editors.js'
import { AT_SAMPLE_MEASURE, RECIPE_LINE_FACTS } from '../builder/copy.js'
import {
  PLANS_GROUP, indexOf, lineOfRecipeDraft, packagePlanViews, recipeDraftOf, serviceNotesPatch,
  type PackageBuilder,
} from '../builder/adapters/package.js'
import { PackageSettingsCard } from './package-settings-card.js'

export function PackageEditorLayer({ b }: { b: PackageBuilder }) {
  const { draft, tree, editing, setEditing, anchor, menu, setMenu } = b
  if (!draft || !tree) return null
  const close = () => setEditing(null)

  return (
    <>
      {menu && <ContextMenu items={menu.items} x={menu.x} y={menu.y} onClose={() => setMenu(null)} />}

      {editing?.kind === 'add' && (
        <AddSearch open anchor={anchor.current} startKind={editing.startKind} hasOpenService={false}
          onClose={close} onPick={(pick) => void b.add(pick, editing.afterLineId)} />
      )}
      {editing?.kind === 'addService' && (
        <AddSearch open mode="service" anchor={anchor.current} hasOpenService={false}
          onClose={close} onPick={(pick) => void b.addService(editing.visitId, pick, editing.afterServiceId)} />
      )}
      {editing?.kind === 'addPackage' && (
        <AddSearch open mode="package" anchor={anchor.current} hasOpenService={false}
          onClose={close} onPick={(pick) => void b.addPackage(pick)} />
      )}

      {editing?.kind === 'margin' && (() => {
        const visit = tree.visits.find((one) => one.id === editing.visitId)
        if (!visit) return null
        return (
          <VisitMarginPopover
            key={visit.id}
            visit={visit}
            anchor={anchor.current}
            onClose={close}
            targetPct={tree.targetMarginPct}
            wholeQuotePct={b.preview?.margin.scopes.base.marginPct ?? null}
            onOpenMargin={() => {
              close()
              b.setMarginScope(visit.margin.base ? 'base' : 'options')
              if (b.railHidden) b.setSummaryOpen(true)
              else document.getElementById('margin-card')?.scrollIntoView({ block: 'start', behavior: 'smooth' })
            }}
          />
        )
      })()}

      {editing?.kind === 'proposal' && (() => {
        const visit = tree.visits.find((one) => one.id === editing.visitId)
        if (!visit) return null
        const [index] = indexOf(visit.id)
        return (
          <ProposalVisitPopover
            open
            anchor={anchor.current}
            visit={visit}
            index={index! + 1}
            current={visit.proposal}
            onClose={close}
            onSave={async (patch) => {
              b.patchVisit(index!, { proposal: { ...visit.proposal, ...patch } })
            }}
          />
        )
      })()}

      {editing?.kind === 'visit' && (
        <PackageVisitEditor key={editing.visitId} b={b} visitId={editing.visitId} section={editing.section} />
      )}

      {editing?.kind === 'service' && (() => {
        const service = tree.visits.flatMap((visit) => visit.services).find((one) => one.id === editing.serviceId)
        const [v, s] = indexOf(editing.serviceId)
        const spec = draft.visits[v!]?.services[s!]
        if (!service || !spec) return null
        return (
          <ServicePopover
            service={service}
            fromPackage
            catalogNotes={spec.catalogNotes ?? NO_SERVICE_NOTES}
            divisions={b.divisions}
            quoteId=""
            propertyId={null}
            quoteReference=""
            canMeasure={false}
            anchor={anchor.current}
            onClose={close}
            onSave={async (patch) => {
              b.patchService(service.id, {
                divisionId: String(patch.divisionId ?? service.divisionId),
                optional: Boolean(patch.optional),
                // The notes the box moved, as this package's own words (0127).
                ...serviceNotesPatch(spec, patch),
              })
              return true
            }}
            onRemove={() => b.removeService(service.id)}
            onResetMeasure={async () => true}
            onClearMeasure={async () => true}
          />
        )
      })()}

      {editing?.kind === 'line' && (() => {
        const [v, s, l] = indexOf(editing.lineId)
        const spec = draft.visits[v!]?.services[s!]?.lines[l!]
        const view = tree.visits[v!]?.services[s!]?.lines[l!]
        const service = tree.visits[v!]?.services[s!]
        if (!spec || !view || !service) return null
        return (
          <RecipeLinePopover
            key={editing.lineId}
            name={view.description}
            kind={view.kind === 'sub' ? 'labor' : view.kind}
            facts={RECIPE_LINE_FACTS(QUOTE_LINE_KIND_LABELS[view.kind], service.name)}
            qtyUnit={view.unit}
            priceUnit={view.unit}
            measureUnit={spec.scalesBy === 'once' && spec.qtyPerUnit === 0 && draft.asks === 'none'
              ? null : b.perOf(spec.scalesBy)}
            typeName={b.types.find((one) => one.id === draft.measurementTypeId)?.name ?? draft.qtyUnit}
            sample={b.sampleValue}
            unitCost={view.costEach ?? 0}
            standard={view.standardRate}
            draft={recipeDraftOf(spec)}
            catalogTaxable={b.preview?.visits.flatMap((one) => one.lines)
              .find((one) => one.key === editing.lineId)?.catalogTaxable ?? view.taxable}
            catalogCustomerName={b.preview?.visits.flatMap((one) => one.lines)
              .find((one) => one.key === editing.lineId)?.catalogCustomerName ?? null}
            onPreview={async (next) => {
              const line = await b.previewLine(editing.lineId, lineOfRecipeDraft(next, spec, draft.asks))
              return line ? { amount: String(line.amount), marginPct: line.marginPct, markupPct: line.markupPct } : null
            }}
            onDone={(next) => b.patchLine(editing.lineId, lineOfRecipeDraft(next, spec, draft.asks))}
            onRemove={() => b.removeLine(editing.lineId)}
            onClose={close}
            anchor={anchor.current}
          />
        )
      })()}

      {editing?.kind === 'billingPlan' && (
        <PackageBillingPlanEditor key={editing.handle} b={b} handle={editing.handle} />
      )}

      {editing?.kind === 'settings' && (
        <SheetOrDialog title="Package settings" onClose={close}>
          <PackageSettingsCard bare draft={draft} types={b.types} units={b.units} canEdit={b.canEdit} onChange={b.patch} />
        </SheetOrDialog>
      )}
    </>
  )
}

/**
 * The quote's visit popover, When block and all (§10.1, Board 8 cell 9): the
 * block in its package mode — *At the start*, no dates — and the When written
 * back through `withWhen` only when Done finds it changed, the quote's own
 * "a Done with no change writes nothing" (§4.7). The Billing field lists the
 * package's real plans (§10.2).
 */
function PackageVisitEditor({ b, visitId, section }: {
  b: PackageBuilder
  visitId: string
  section?: 'name' | 'schedule' | 'billing' | 'notes' | 'choices'
}) {
  const [index] = indexOf(visitId)
  const spec = b.draft!.visits[index!]
  const view = b.tree!.visits[index!]
  if (!spec || !view) return null

  return (
    <VisitPopover
      visit={view}
      plans={packagePlanViews(b.draft!)}
      options={b.tree!.chooseOptions}
      groups={b.tree!.chooseGroups}
      onCreateChoice={async (name) => ({ id: b.addPlan(name), groupId: PLANS_GROUP })}
      onRenameQuestion={async (_groupId, name) => { b.patch({ proposalText: name }); return true }}
      people={[]}
      anchor={b.anchor.current}
      section={section}
      inPackage
      hasRental={b.hasRental}
      totalLabel={AT_SAMPLE_MEASURE}
      /*
       * The package's visits for *Which visit*; the proposal's line in the
       * visit's own timing mode, else *frequency* — a package has no dates for
       * any other mode to say (Board 8 cell 9: “Every 6 weeks.”).
       */
      quote={{ visits: b.tree!.visits, proposalTiming: 'frequency', proposalDatesAllowed: false }}
      onSaveWhen={async (when) => { b.setVisitWhen(visitId, when); return true }}
      onClose={() => b.setEditing(null)}
      onSave={async (patch) => {
        const over: Partial<PackageVisitSpec> = {
          name: String(patch.name ?? '').trim() || spec.name,
          ...(patch.role ? { role: patch.role as PackageVisitSpec['role'] } : {}),
          billingPlanId: (patch.billingPlanId as string | null | undefined) ?? null,
          optional: Boolean(patch.optional),
          notes: {
            quote: String(patch.noteQuote ?? ''),
            internal: String(patch.noteInternal ?? ''),
            crew: String(patch.noteCrew ?? ''),
          },
        }
        b.patchVisit(index!, over)
        if (Array.isArray(patch.optionIds)) b.setMembership(visitId, patch.optionIds as string[])
        return true
      }}
      onRemove={b.draft!.visits.length > 1 ? () => b.removeVisit(index!) : undefined}
    />
  )
}

/**
 * A billing plan on a package, in the engine's 480 popover (§10.2): Board 7's
 * chrome — tile, name, what it is, ✕ — around Board 16's phone sheet, because
 * a 480 panel and a phone sheet are the same width problem. Terms, the five
 * kinds as a column with the chosen one's rows, Reads, and its stages. The
 * contract's removals: no billed window, no stage *On a date*, no invoices
 * table and no totals — a package has no dates and no money until a quote.
 */
function PackageBillingPlanEditor({ b, handle }: { b: PackageBuilder; handle: string }) {
  const plans = packagePlanViews(b.draft!)
  const plan = plans.find((one) => one.id === handle)
  if (!plan) return null
  const visits = b.tree!.visits
  const ctx = planWordsContext(plan, visits)
  const onPatch = async (body: Record<string, unknown>) => { b.editBillingPlan(handle, body) }
  const close = () => b.setEditing(null)

  return (
    <EditorShell
      open
      onClose={close}
      anchor={b.anchor.current}
      label={plan.name}
      header={(
        <EditorHeader kind="invoice" icon={IconMoney} name={plan.name}
          facts={`${BILLING_KIND_CARDS[plan.kind].title} · ${BILLING_TAB.visitsChip(plan.visitIds.length)}`} />
      )}
      footer={(
        <EditorActions
          onRemove={b.canEdit ? () => { b.removeBillingPlan(handle); close() } : undefined}
          onDone={close}
        />
      )}
    >
      <div className="flex flex-col gap-4" data-package-plan="">
        <div className="flex flex-col gap-1.5">
          <span className="text-[12.5px] text-text-muted">{TERMS_WORDS.label}</span>
          <TermsSelect full termsMode={plan.termsMode} termsDays={plan.termsDays}
            customerDays={plan.customerTermsDays} disabled={!b.canEdit}
            onChange={(change) => void onPatch(change)} />
        </div>
        {b.canEdit && (
          <KindPanel plan={plan} ctx={ctx} visits={visits} layout="phone" busy={false} onPatch={onPatch} dateless />
        )}
      </div>
    </EditorShell>
  )
}
